<template>
  <div v-if="show">
    <div class="form-group row form no-gutters" 
      v-for="(field, index) in allFormFields" 
      :key="index" v-show="!field.hidden" 
      v-if="toDisplay(field)"
    >
      <label
        :for="field.name"
        :class="[
          'col-12 col-md-4 col-form-label d-flex field-label',
          field.help_text ? 'align-items-start pt-2' : 'align-items-center'
        ]"
      >
        <span class="label-text">
          {{ field.label?.replace('*', '') || field.placeholder?.replace('*', '') }}<span
            v-if="(field.required === 'true' || field.required || field.validations?.required)"
            class="text-danger"
          >*</span>
        </span>
      </label>

      <div class="col-12 col-md-8 d-flex align-items-center">
        <ValidationProvider
          :rules="getValidationRules(field)"
          v-slot="{ errors }"
          class="form-field-container"
          :name="field.name"
          :vid="field.name"
        >
          <input
            class="form-control input-text"
            :class="[isUserEditPage ? 'input-field' : '', errors[0] ? 'box-alert' : '']"
            v-if="isTextInput(field)"
            v-model="model[field.name]"
            :type="field.type"
            :placeholder="field.placeholder || field.label"
            :disabled="field.disabled === 'true'"
            :required="field.required === 'true'"
            :id="idPrefix + '-' + field.name"
            v-mask="field.mask"
            :style="field.only_uppercase ? 'text-transform: uppercase;' : ''"
            @input="handleInput(field, $event.target.value)"
          />

          <input
            class="form-control input-text"
            :class="[isUserEditPage ? 'input-field' : '', errors[0] ? 'box-alert' : '']"
            type="email"
            v-else-if="field.type === 'email'"
            v-model="model[field.name]"
            :placeholder="field.placeholder || field.label"
            :disabled="field.disabled === 'true'"
            :required="field.required === 'true'"
            :id="idPrefix + '-' + field.name"
          />

          <select
            class="form-control input-text"
            :class="[isUserEditPage ? 'input-field' : '', errors[0] ? 'box-alert' : '', model[field.name] === '' ? 'text-black-50' : '']"
            v-else-if="field.type === 'select'"
            v-model="model[field.name]"
            :disabled="field.disabled === 'true'"
            :id="idPrefix + '-' + field.name"
          >
            <option disabled value="">{{ field.placeholder || 'Please select one' }}</option>
            <option v-for="(text, value) in getFieldOptions(field)" :value="value" :key="value">
              {{ text }}
            </option>
          </select>

          <textarea
            v-else-if="field.type === 'textarea'"
            class="form-control input-text"
            :class="[isUserEditPage ? 'input-field' : '', errors[0] ? 'box-alert' : '']"
            :id="idPrefix + '-' + field.name"
            v-model="model[field.name]"
            :placeholder="field.placeholder || field.label"
            :disabled="field.disabled === 'true'"
            :required="field.required === 'true'"
          ></textarea>

          <small class="w-100 text-black-50" v-if="field.help_text">{{ field.help_text }}</small>
          
          <div class="validation-error">
            <span class="error-message w-100 text-red-50" v-if="errors[0]" :class="errors[0] ? 'has-error' : ''">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate';

export default {
  name: 'FormFieldTemplate',

  components: {
    ValidationProvider,
  },

  props: {
    formFields: Array,
    show: Boolean,
    model: Object,
    idPrefix: String,
    isCopyAddress: Boolean,
    enableSignupIntegrated: Boolean,
    isFulFillSyncAllowed: Boolean,
    isUserEditPage: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      allFormFields: this.formFields,
      sitesFields: [],
      windowWidth: window.innerWidth,
    }
  },

  created() {
    window.addEventListener('resize', this.updateWindowWidth);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateWindowWidth);
  },

  mounted() {
    let hasUtilityService = this.allFormFields.find(field => field.name === 'Utility_Service_Provider__c');

    if (this.isFulFillSyncAllowed && hasUtilityService) {
      this.$store.dispatch('users/fetchSiteFields').then((response) => {
        this.sitesFields = response.data;
        const utilityField = this.sitesFields.find(field => field.name === 'Utility_Service_Provider__c');

        if (utilityField) {
          const isRequired = utilityField.required === 'true';
          hasUtilityService.placeholder = `${utilityField.label}${isRequired ? '*' : ''}`;
          hasUtilityService.label = `${utilityField.label}${isRequired ? '*' : ''}`;
          hasUtilityService.validations = {
            required: isRequired,
          };
          hasUtilityService.options = utilityField.picklist_values.reduce((acc, value) => {
            acc[value.value] = value.label;
            return acc;
          }, {});
        }
      });
    }
  },

  methods: {
    isTextInput(field) {
      const textTypes = ['text', 'number', 'password'];
      return textTypes.includes(field.type);
    },

    getValidationRules(field) {
      const validationList = [];

      if (field.required === 'true' || field.required || field.validations?.required) {
        validationList.push('required');
      }

      if (field.type === 'email') {
        validationList.push('email');
      }

      return validationList.join('|');
    },

    getFieldOptions(field) {
      if (field.parent !== undefined) {
        const parentValue = this.model[field.parent];

        if (field.inverse_options) {
          const options = {};
          Object.keys(field.options).forEach((key) => {
            if (field.options[key].includes(parentValue)) options[key] = key;
          });
          return options;
        }

        return field.options[parentValue];
      }

      return field.options;
    },

    handleInput(field, value) {
      if (field.only_uppercase) {
        this.model[field.name] = value.toUpperCase();
      } else {
        this.model[field.name] = value;
      }
    },

    updateWindowWidth() {
      this.windowWidth = window.innerWidth;
    },

    toDisplay(field) {
      if (field.parent !== undefined && !field.mappings) {
        return field.parent_value.includes(this.model[field.parent]);
      }
      return true;
    },
  },

};

</script>

<style lang="scss" scoped>
@import '../../assets/stylesheets/pages/user_edit.scss';

.input-field {
  font-size: 1.25rem;
  line-height: 1.5;
}

.form-field-container {
  width: 100%;
}

@media (max-width: 1200px) {
  .form-field-container {
    max-width: 100%;
    margin-left: 0;
  }
}
.label-text {
  display: inline;
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: auto;
}

</style>
